/**
 * @module  public/js/services/aiReportService.js
 * @class   AiReportService
 * @summary Fetches contact/company AI reports with per-session caching.
 *
 * @description
 * Hides the HTTP details & caching behind simple public methods:
 *  • `getContactReport(contactId)`
 *  • `getCompanyReport(accountId)`
 *
 * ## Dependencies (DI)
 * – apiClient, endpoints
 *
 * @version 1.0.0
 * @since   2025-05-02
 */

import useTenant from '../useTenant.js';
import { docWithTenant } from '../utils/firestoreHelpers.js';
import { getDoc } from 'https://www.gstatic.com/firebasejs/9.17.2/firebase-firestore.js';
import { getCurrentCRM } from './crmUtils.js';

export default class AiReportService {
  #apiClient;

  #endpoints;

  /** @type {Record<string, string>} */
  #contactCache = {};

  /** @type {Record<string, string>} */
  #companyCache = {};

  /**
   * @param {object} deps
   * @param {import('../core/apiclient.js').default} deps.apiClient
   * @param {import('../core/endpoints-service.js').default} deps.endpoints
   */
  constructor({ apiClient, endpoints }) {
    this.#apiClient = apiClient;
    this.#endpoints = endpoints;
  }

  /** @returns {Promise<string>} raw AI string or '' */
  async getContactReport(contactId, userId) {
    if (this.#contactCache[contactId]) return this.#contactCache[contactId];
    let crm = 'local';
    try {
      crm = await getCurrentCRM(userId);
    } catch {
      crm = 'local';
    }
    if (crm === 'local') {
      const raw = await this.#getContactReportFromMeta(contactId);
      this.#contactCache[contactId] = raw;
      return raw;
    }
    const url = this.#endpoints.getContactDetails({ userId, contactId });
    try {
      const { contact = {} } = await this.#apiClient.fetchJson(url, {
        auth: true,
      });
      const raw =
        contact.properties?.topsail_analysis_contact_report_focusmode ||
        contact.properties?.ai_analysis_contact_report_focusmode ||
        '';
      this.#contactCache[contactId] = raw;
      return raw;
    } catch {
      const raw = await this.#getContactReportFromMeta(contactId);
      this.#contactCache[contactId] = raw;
      return raw;
    }
  }

  /**
   * Fallback: fetch contact report from tenant-scoped ContactsMeta.
   *
   * @param {string} contactId
   * @returns {Promise<string>} raw AI string or ''
   * @private
   */
  async #getContactReportFromMeta(contactId) {
    try {
      const tenantId = useTenant();
      if (!tenantId) return '';
      const ref = docWithTenant(tenantId, `ContactsMeta/${contactId}`);
      const snap = await getDoc(ref);
      if (!snap.exists()) return '';
      const data = snap.data();
      return (
        data.topsail_analysis_contact_report_focusmode ||
        data.ai_analysis_contact_report_focusmode ||
        ''
      );
    } catch {
      return '';
    }
  }

  /** @returns {Promise<string>} raw AI string or '' */
  async getCompanyReport(accountId, userId) {
    if (this.#companyCache[accountId]) return this.#companyCache[accountId];
    const url = this.#endpoints.getAccountDetails({ userId, accountId });
    const { properties = {} } = await this.#apiClient.fetchJson(url, {
      auth: true,
    });
    const raw =
      properties.topsail_analysis_company_report_focusmode ||
      properties.ai_analysis_company_report_focusmode ||
      '';
    this.#companyCache[accountId] = raw;
    return raw;
  }
}
